import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { Edit, Check, Info } from 'lucide-react';

import BaseModal from '@/shared/components/modals/core/BaseModal';
import Button from '@/shared/components/elements/Button';
import Input from '@/shared/components/elements/Input';
import { gatewayUpdateSchema } from '@/feature/Gateway/schemas/gatewaySchemas';
import { extractFieldErrors } from '@/shared/utils/errorUtils';

function GatewayModal({
  isOpen,
  onClose,
  onSubmit,
  gateway,
  isLoading = false,
}) {
  const [serverErrors, setServerErrors] = useState({});

  const form = useForm({
    defaultValues: {
      name: gateway?.name || '',
    },
    validatorAdapter: zodValidator(),
    onSubmit: async ({ value }) => {
      try {
        setServerErrors({});
        await onSubmit({
          ...value,
          gateway_id: gateway.id,
        });
        form.reset();
        onClose();
      } catch (error) {
        console.error('Gateway update error:', error);
        const fieldErrors = extractFieldErrors(error);
        if (Object.keys(fieldErrors).length > 0) {
          setServerErrors(fieldErrors);
        }
      }
    },
  });

  useEffect(() => {
    if (isOpen && gateway) {
      form.setFieldValue('name', gateway.name || '');
      setServerErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, gateway]);

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="ویرایش هاب"
      icon={<Edit size={20} />}
      iconVariant="primary"
      size="md"
      closeOnBackdropClick={!isLoading}
      closeOnEscape={!isLoading}
      showCloseButton={!isLoading}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            fullWidth
            onClick={onClose}
            disabled={isLoading}
          >
            انصراف
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            icon={<Check size={18} />}
            iconPosition="left"
            onClick={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            ذخیره تغییرات
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="space-y-4 p-4 sm:space-y-5 sm:p-6"
      >
        {/* Info Box */}
        <div className="border-tertiary-10 bg-tertiary-6 rounded-lg border p-3 backdrop-blur-sm transition-colors duration-200 sm:rounded-xl sm:p-4 dark:border-gray-500/30 dark:bg-gray-500/10">
          <div className="flex items-start gap-2">
            <Info
              size={16}
              className="text-tertiary-80 mt-0.5 flex-shrink-0 transition-colors duration-200 dark:text-gray-400"
            />
            <div className="min-w-0 flex-1">
              <p className="text-tertiary-100 text-xs font-semibold break-words transition-colors duration-200 dark:text-gray-300">
                EUI: {gateway?.eui}
              </p>
              <p className="text-tertiary-60 mt-1 text-xs transition-colors duration-200 dark:text-gray-500">
                EUI قابل تغییر نیست
              </p>
            </div>
          </div>
        </div>

        {/* Form Field */}
        <form.Field
          name="name"
          validators={{
            onChange: gatewayUpdateSchema.shape.name,
          }}
        >
          {(field) => {
            const fieldError = field.state.meta.errors?.[0];
            const errorMessage = fieldError
              ? typeof fieldError === 'string'
                ? fieldError
                : fieldError.message || 'خطای اعتبارسنجی'
              : serverErrors.name;

            return (
              <Input
                label="نام هاب"
                type="text"
                name="name"
                placeholder="نام هاب را وارد کنید"
                value={field.state.value}
                onChange={(e) => {
                  field.handleChange(e.target.value);
                  if (serverErrors.name) {
                    setServerErrors((prev) => ({
                      ...prev,
                      name: undefined,
                    }));
                  }
                }}
                error={errorMessage}
                disabled={isLoading}
                required
              />
            );
          }}
        </form.Field>
      </form>
    </BaseModal>
  );
}

GatewayModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  gateway: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    eui: PropTypes.string,
  }).isRequired,
  isLoading: PropTypes.bool,
};

GatewayModal.defaultProps = {
  isLoading: false,
};

export default GatewayModal;
